import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DialogFixUtil } from '@/components/ui/dialog-fix-util';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileUp, Loader2, X, AlertCircle, CheckCircle, RotateCw, Download } from 'lucide-react';
import { associationApi } from '@/services/api';
import { CSVFileInput } from '@/components/shared/CSVFileInput';
import { useCSVUpload } from '@/hooks/use-csv-upload';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { BulkUploadResponse } from '@/types/upload';

interface BulkUploadAssociationsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BulkUploadAssociations: React.FC<BulkUploadAssociationsProps> = ({ 
  open, 
  onOpenChange 
}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Use management company ID 1 for testing purposes
  // In a real app, you'd get the current management company ID from auth context
  const managementCompanyId = 1;

  const handleUploadSuccess = () => {
    // Refetch associations data
    queryClient.invalidateQueries({ queryKey: ['associations'] });
    
    // Close the modal
    onOpenChange(false);
  };

  const {
    file,
    error,
    uploading,
    importResult,
    handleFileChange,
    handleSubmit,
    handleCancel,
    resetResult
  } = useCSVUpload<BulkUploadResponse>({
    uploadFn: (file) => associationApi.bulkUpload(file, managementCompanyId),
    onSuccess: handleUploadSuccess,
    onCancel: () => {
      // Only close if not uploading
      if (!uploading) {
        onOpenChange(false);
      }
    }
  });

  const handleDialogChange = (open: boolean) => {
    if (!uploading) {
      // Only allow closing if not currently uploading
      onOpenChange(open);
    }
  };

  // Safe close handler that checks uploading status
  const handleClose = () => {
    if (!uploading) {
      handleCancel();
    }
  };

  // Handle retry after seeing errors
  const handleRetry = () => {
    resetResult();
  };

  // Handle continue after seeing errors
  const handleContinue = () => {
    if (importResult) {
      handleUploadSuccess();
    }
  };

  // Handle export current associations to CSV
  const handleExportCSV = async () => {
    try {
      toast({
        title: "Exporting associations",
        description: "Preparing CSV file...",
      });
      
      const csvContent = await associationApi.exportCsv(managementCompanyId);
      
      // Create a download link for the CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'associations.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export completed",
        description: "Associations data has been downloaded as a CSV file.",
      });
    } catch (error) {
      console.error("Error exporting associations:", error);
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : "Failed to export associations",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogFixUtil open={open} />
      <DialogContent 
        className={`overflow-hidden ${importResult?.errors?.length ? 'max-w-3xl' : 'max-w-md'}`}
        onPointerDownOutside={(e) => {
          // Prevent closing when uploading
          if (uploading) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          // Prevent closing when uploading
          if (uploading) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk Upload Associations</DialogTitle>
          <DialogDescription>
            Upload a CSV file with association data or export current associations.
          </DialogDescription>
        </DialogHeader>
        
        {importResult?.errors && importResult.errors.length > 0 ? (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Import Result</AlertTitle>
              <AlertDescription>
                Successfully imported {importResult.imported} out of {importResult.total || importResult.imported + importResult.errors.length} associations.
                Found {importResult.errors.length} errors:
              </AlertDescription>
            </Alert>
            
            <div className="max-h-[300px] overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importResult.errors.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="text-sm text-destructive">{error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleRetry}>
                <RotateCw className="mr-2 h-4 w-4" />
                Upload Another File
              </Button>
              <Button onClick={handleContinue}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Continue
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={(e) => handleSubmit(e)} encType="multipart/form-data" className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input 
                    type="file"
                    id="csv-file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="flex-1"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleExportCSV}
                    title="Export associations to CSV"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
                {file && (
                  <div className="text-sm text-muted-foreground flex items-center mt-1">
                    <FileUp className="h-4 w-4 mr-2" />
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </div>
                )}
                {error && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </div>
              
              <Alert>
                <AlertDescription>
                  <p className="font-medium mb-1">Required CSV format:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Required columns: name, association_type</li>
                    <li>Optional: external_id, pricing_table_name, document_group_names (separated by semicolons)</li>
                    <li>Updates are matched by external_id</li>
                    <li>Use quotes for values containing commas (e.g. "Bellasol Condominium 1 Association, Inc")</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!file || uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FileUp className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        <button
          onClick={handleClose}
          disabled={uploading}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          aria-label="Close dialog"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </DialogContent>
    </Dialog>
  );
};
